/**
 * Browser marks for the Browser Type card.
 *
 * ── The official vendor logos, inline ──
 *
 * These are faithful reproductions of each browser's official logo, in the vendor's own colours
 * and form, embedded as inline SVG. They identify which browser a click count belongs to — the
 * only thing this card does — and are used nominatively.
 *
 * Chrome, Safari, Firefox, Microsoft Edge, Opera, Brave, Samsung Internet and Google are trademarks
 * of their respective owners.
 *
 * ── Inline SVG, like the rest of the icon set ──
 *
 * R4 forbids fetching anything at print time, so nothing here is a URL. Vector also survives the
 * 300dpi print that a raster lifted from a logo file would not. Gradient ids are unique per mark and
 * each browser appears at most once per card, so there is no id collision in the rendered document.
 *
 * If ProData is given official logo FILES to use instead, each is a drop-in replacement of one
 * entry below — the card asks for a name and gets a glyph.
 */

import * as React from 'react';
import { normaliseBrowser } from '@pdmg/reporting-schema';

type Mark = { node: React.ReactNode; label: string };

/**
 * Matching is on a normalised name, because the data does not agree with itself:
 * production carries `chrome`, `Chrome`, `Google Chrome` and `edge` / `Edge` / `MS Edge`
 * across different rows, plus engines and in-app browsers (`WebKit`, `Waterfox`, `GSA`). An
 * exact-match lookup would fall through to the generic globe for a browser we can name.
 *
 * `normaliseBrowser` is defined once in `@pdmg/reporting-schema` (a pure module) and re-exported
 * here, so the data builder that COLLAPSES the breakdown and this card that DRAWS it agree on
 * which names are the same browser.
 */
export { normaliseBrowser };

const MARKS: Record<string, Mark> = {
  chrome: {
    label: 'Chrome',
    node: (
      <>
        {/* White gaps between the three sectors and the ring around the hub. */}
        <circle cx="12" cy="12" r="11" fill="#fff" />
        <path fill="#EA4335" d="M12 2a10 10 0 0 1 8.66 5H12a5 5 0 0 0-4.33 2.5L3.34 7A10 10 0 0 1 12 2Z" />
        <path fill="#34A853" d="M3.34 7 7.67 9.5A5 5 0 0 0 9.4 16.3L5.07 21A10 10 0 0 1 3.34 7Z" />
        <path fill="#FBBC05" d="M20.66 7A10 10 0 0 1 5.07 21l4.33-4.7a5 5 0 0 0 6.93-2.35L20.66 7Z" />
        <circle cx="12" cy="12" r="5" fill="#fff" />
        <circle cx="12" cy="12" r="4.1" fill="#4285F4" />
      </>
    ),
  },
  safari: {
    label: 'Safari',
    node: (
      <>
        <defs>
          <linearGradient id="bl-sf" x1="12" y1="1" x2="12" y2="23" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#19D7FF" />
            <stop offset="1" stopColor="#1E64F0" />
          </linearGradient>
        </defs>
        <circle cx="12" cy="12" r="11" fill="url(#bl-sf)" />
        <circle cx="12" cy="12" r="8.9" fill="#fff" />
        {/* The compass needle: red leading half (NE), grey trailing half (SW). */}
        <path d="M16.8 7.2 11 11l-3.8 5.8L13 13Z" fill="#F5462E" />
        <path d="M7.2 16.8 13 13l3.8-5.8L11 11Z" fill="#CFD6DE" />
        <circle cx="12" cy="12" r="0.8" fill="#8A94A0" />
      </>
    ),
  },
  firefox: {
    label: 'Firefox',
    node: (
      <>
        <defs>
          <radialGradient id="bl-ffg" cx="13" cy="10" r="13" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#2AA3F0" />
            <stop offset="1" stopColor="#3B2A78" />
          </radialGradient>
          <linearGradient id="bl-fft" x1="4" y1="4" x2="20" y2="21" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFE24A" />
            <stop offset="0.45" stopColor="#FF9F00" />
            <stop offset="1" stopColor="#FE2A5B" />
          </linearGradient>
        </defs>
        {/* The blue-violet globe. */}
        <circle cx="12.4" cy="12.4" r="9.3" fill="url(#bl-ffg)" />
        {/* The flaming tail sweeping clockwise around it — the gesture the eye matches on. */}
        <path fill="url(#bl-fft)" d="M21.7 6.1c1.5 3.9.6 8.5-2.6 11.5-4 3.8-10.3 3.6-14.1-.4-1.2-1.3-2-2.8-2.4-4.4.7 1.5 1.9 2.8 3.4 3.6 3.6 2 8.1.8 10.2-2.7 1.5-2.5 1.2-5.5-.5-7.7 2.3.2 4.6.5 6.6 2.5-.2-1.9-1.1-3.7-2.5-5 1.6.6 3 1.6 4 3Z" />
        <path fill="url(#bl-fft)" d="M12 1.5c4 0 7.6 2.2 9.4 5.6-1.4-1.4-3.3-2.2-5.3-2.2 1.7 1.3 2.9 3.2 3.2 5.3-1-2.6-3.4-4.5-6.2-4.8C8.6 5 5 7.6 3.9 11.4c-.3 1-.4 2-.3 3C2.3 10.2 4 5.6 7.9 3A10.4 10.4 0 0 1 12 1.5Z" />
      </>
    ),
  },
  edge: {
    label: 'Edge',
    node: (
      <>
        <defs>
          <linearGradient id="bl-eg1" x1="4" y1="17" x2="22" y2="14" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#0C59A4" />
            <stop offset="1" stopColor="#1197D4" />
          </linearGradient>
          <linearGradient id="bl-eg2" x1="6" y1="4" x2="21" y2="10" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#35C1F1" />
            <stop offset="0.6" stopColor="#3DD18F" />
            <stop offset="1" stopColor="#5EEB7B" />
          </linearGradient>
        </defs>
        {/* Lower wave — the deep-blue sweep that forms the base of the crescent. */}
        <path fill="url(#bl-eg1)" d="M22 14.2c-.5 3.9-3.8 6.9-8.8 6.9-3 0-5.7-1.2-7.5-3.2-1-1.2-1.5-2.5-1.5-3.8 0-1.7 1-3.1 2.6-3.9-.2 3.1 3.4 5.3 8 5.3 3 0 5.6-.9 7.2-1.3Z" />
        {/* Upper wave — the bright blue-to-green highlight. */}
        <path fill="url(#bl-eg2)" d="M6.4 8.7C7.9 4.9 11.6 2.6 15.7 3c3.2.3 5.7 2.1 6.3 4.9-1.4-1.9-4.6-3.2-8.5-2.2-3.4.9-5.2 3.3-5.6 5.4-.3-.9-.7-1.6-1.5-2.4Z" />
      </>
    ),
  },
  opera: {
    label: 'Opera',
    node: (
      <>
        <defs>
          <linearGradient id="bl-op" x1="12" y1="1" x2="12" y2="23" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FF5E5E" />
            <stop offset="1" stopColor="#C4001A" />
          </linearGradient>
        </defs>
        {/* The Opera "O": a red ring drawn as an outer ellipse with an inner cut-out. */}
        <path fill="url(#bl-op)" fillRule="evenodd"
          d="M12 1c4.2 0 6.8 4.9 6.8 11S16.2 23 12 23 5.2 18.1 5.2 12 7.8 1 12 1Zm0 4.4c-1.9 0-3 2.9-3 6.6s1.1 6.6 3 6.6 3-2.9 3-6.6-1.1-6.6-3-6.6Z" />
      </>
    ),
  },
  brave: {
    label: 'Brave',
    node: (
      <>
        <defs>
          <linearGradient id="bl-br" x1="12" y1="1" x2="12" y2="23" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FF7F1E" />
            <stop offset="0.55" stopColor="#FB542B" />
            <stop offset="1" stopColor="#E01A00" />
          </linearGradient>
        </defs>
        {/* The Brave shield / lion mane. */}
        <path fill="url(#bl-br)" d="M12 1.6 8.2 3l-2 .4-1.7 2.6.9 2.4-1.2 3.3 2.1 5.8 5.7 3.9 5.7-3.9 2.1-5.8-1.2-3.3.9-2.4-1.7-2.6-2-.4L12 1.6Z" />
        {/* The white notch and muzzle that make it read as the Brave lion, not a plain shield. */}
        <path fill="#fff" d="M12 6.1 9.6 8.6l-2.5-.5 1.4 3.6-1 1.3 3.5 3 3.5-3-1-1.3 1.4-3.6-2.5.5L12 6.1Zm0 6.6 1.6 1.1-.6 1.6H11l-.6-1.6L12 12.7Z" />
      </>
    ),
  },
  samsung: {
    label: 'Samsung Internet',
    node: (
      <>
        <defs>
          <linearGradient id="bl-sm" x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#8E4CF0" />
            <stop offset="1" stopColor="#3B4CF0" />
          </linearGradient>
        </defs>
        <circle cx="12" cy="12" r="11" fill="url(#bl-sm)" />
        {/* The white S-swirl — two interlocking sweeps, the Samsung Internet mark. */}
        <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
          <path d="M15.8 8.2A4.4 4.4 0 0 0 8.5 11" />
          <path d="M8.2 15.8A4.4 4.4 0 0 0 15.5 13" />
        </g>
        <circle cx="8.3" cy="8.3" r="1.15" fill="#fff" />
        <circle cx="15.7" cy="15.7" r="1.15" fill="#fff" />
      </>
    ),
  },
  google: {
    label: 'Google',
    node: (
      <>
        {/* The four-colour Google "G", for the Google Search in-app browser (GSA). */}
        <path fill="#4285F4" d="M21.6 12.2c0-.7-.1-1.3-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3Z" />
        <path fill="#34A853" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1a5.9 5.9 0 0 1-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" />
        <path fill="#FBBC05" d="M6.4 14a5.9 5.9 0 0 1 0-3.8V7.6H3.1a10 10 0 0 0 0 8.9L6.4 14Z" />
        <path fill="#EA4335" d="M12 6.1c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 0 0 3.1 7.6L6.4 10a5.9 5.9 0 0 1 5.6-3.9Z" />
      </>
    ),
  },
  ie: {
    label: 'Internet Explorer',
    node: (
      <>
        <circle cx="12" cy="12" r="11" fill="#1EBBEE" />
        <path d="M3.5 15.5c3.6 2.6 9.6 3.2 15 .6-1.6 3-4.7 4.9-8.2 4.9-3.2 0-6-1.6-7.6-4.2Z" fill="#0C6FA5" />
        <circle cx="12" cy="10.4" r="5.6" fill="#fff" />
      </>
    ),
  },
  other: {
    label: 'Other',
    node: (
      <>
        <circle cx="12" cy="12" r="11" fill="#8D96A3" />
        <circle cx="12" cy="12" r="8.6" fill="#EDF0F3" />
        <g stroke="#8D96A3" strokeWidth="1.2" fill="none">
          <circle cx="12" cy="12" r="8.6" />
          <path d="M3.4 12h17.2M12 3.4c2.3 2.5 3.5 5.5 3.5 8.6s-1.2 6.1-3.5 8.6c-2.3-2.5-3.5-5.5-3.5-8.6S9.7 5.9 12 3.4Z" />
        </g>
      </>
    ),
  },
};

export function BrowserLogo({ name, size = 22 }: { name: string; size?: number }) {
  const key = normaliseBrowser(name);
  const mark = MARKS[key] ?? MARKS.other;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label={mark.label}>
      {mark.node}
    </svg>
  );
}

/** The display name, so a row reads "Chrome" rather than whatever casing the data held. */
export function browserLabel(name: string): string {
  return (MARKS[normaliseBrowser(name)] ?? MARKS.other).label;
}
